/**
 * Staff permissions, the same rule as the server's `loadStaffPermissionCheck` (permission.middleware.ts): a staff
 * member holds their own permissions plus every role's; `<key>:forbidden` overrides a grant; the `admin` role holds
 * everything. One rule for admin-dashboard, pos-web and pos-android (POS.md §18.7 Q5). The server still enforces every
 * route; the apps use this only to show what the staff member may do.
 */

export const ADMIN_ROLE_KEY = 'admin'

export interface PermissionHolder {
  permissions?: string[] | null
  roles?: { key?: string | null; permissions?: string[] | null }[] | null
}

export interface PermissionSet {
  isAdmin: boolean
  granted: string[]
}

export const effectivePermissions = (staff: PermissionHolder | null | undefined): PermissionSet => {
  const roles = staff?.roles ?? []
  const granted = new Set<string>(staff?.permissions ?? [])
  roles.forEach((role) => (role.permissions ?? []).forEach((p) => granted.add(p)))
  return { isAdmin: roles.some((role) => role.key === ADMIN_ROLE_KEY), granted: [...granted] }
}

export const hasPermission = (set: PermissionSet | null | undefined, permission: string): boolean => {
  if (!set) return false
  if (set.isAdmin) return true
  return !set.granted.includes(`${permission}:forbidden`) && set.granted.includes(permission)
}

/** The `/profile` graph that returns what `effectivePermissions` needs. */
export const PROFILE_PERMISSION_GRAPH = { '*': true, roles: { '*': true } } as const

/**
 * Fields only staff holding the permission may see or write; the server strips them otherwise (mirror of
 * `iws_commerce_server/config/field_permissions.ts`, keep both in step). Apps hide the matching inputs.
 */
export const FIELD_PERMISSIONS: { permission: string; fields: string[] }[] = [
  { permission: 'products:view_cost', fields: ['purchase_cost', 'purchase_price', 'unit_purchase_price'] },
]

/** Whether the staff member may see a field-permission field (true for fields no rule covers). */
export const canSeeField = (set: PermissionSet | null | undefined, field: string): boolean =>
  FIELD_PERMISSIONS.every((rule) => !rule.fields.includes(field) || hasPermission(set, rule.permission))
